import { useCallback } from 'react';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Team } from '../../../store/Team';
import { Permission } from '../../../store/GeneralStore';
import { hasPermissions } from '../../../store/User';
import {
  assignPermissionSet,
  unassignPermissionSet,
  usePermissionSetAssignments,
  usePermissionSets
} from '../../../store/PermissionSets';
import { DetailViewCard } from '../CardWithTitle';
import { PermissionSetAssignmentRow } from './PermissionSetAssignmentRow';
import { assignmentForTeam } from './userAssignmentHelpers';

export interface TeamDrawerPermissionSetSectionProps {
  team: Team;
}

export const TeamDrawerPermissionSetSection = ({ team }: TeamDrawerPermissionSetSectionProps) => {
  const { config } = useFeatureFlags();
  if (!config.permission_sets) return null;
  return <TeamDrawerPermissionSet team={team} />;
};

const TeamDrawerPermissionSet = ({ team }: TeamDrawerPermissionSetSectionProps) => {
  const { setSnack } = useSnackbar();
  const { permissionSets } = usePermissionSets();
  const { assignments, submitting } = usePermissionSetAssignments();
  const canAssignTeam = hasPermissions([Permission.ASSIGN_TEAM]);
  const current = assignmentForTeam(assignments, team.id);

  const handleChange = useCallback(
    async (value: string) => {
      const result = value
        ? await assignPermissionSet({ permission_set_id: value, team_id: Number(team.id) })
        : current
          ? await unassignPermissionSet(current.id)
          : null;
      if (result?.error)
        setSnack({ message: result.error.message || 'Could not update permission set', type: 'error', open: true });
    },
    [team.id, current, setSnack]
  );

  return (
    <DetailViewCard
      key="team_permission_set"
      title="Permission Set">
      <PermissionSetAssignmentRow
        label={team.name}
        value={current?.permission_set_id ?? ''}
        permissionSets={permissionSets}
        emptyLabel="None"
        ariaLabel={`${team.name} permission set`}
        canEdit={canAssignTeam}
        submitting={submitting}
        onChange={handleChange}
      />
    </DetailViewCard>
  );
};
